import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { ApiError } from '@/shared/api/errors';
import { noticeKeys } from '@/shared/notices/api';

import { type TariffSearchParams, tariffKeys, tariffsApi } from './api';
import {
  type ColumnsWrite,
  type FromAttachment,
  type PublishRequest,
  type Supplier,
  type SupplierConditionWrite,
  type SupplierUpdate,
  type SupplierWrite,
  type Version,
  WORKING,
} from './types';

export function useSuppliers(q = '', offset = 0, enabled = true) {
  return useQuery({
    queryKey: tariffKeys.supplierList(q, offset),
    queryFn: () => tariffsApi.suppliers(q, offset),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useSupplier(id: string | undefined) {
  return useQuery<Supplier, ApiError>({
    queryKey: tariffKeys.supplier(id ?? ''),
    queryFn: () => tariffsApi.supplier(id ?? ''),
    enabled: Boolean(id),
  });
}

/**
 * A version, asked again while the worker has it: every two seconds while it reads, every
 * ten once it looks stuck (the screen offers to ask again by then), never once it waits
 * for a person or is done.
 */
export function useVersion(id: string | undefined) {
  return useQuery<Version, ApiError>({
    queryKey: tariffKeys.version(id ?? ''),
    queryFn: () => tariffsApi.version(id ?? ''),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const version = query.state.data;
      if (!version || !WORKING.has(version.status)) return false;
      return version.stuck ? 10_000 : 2_000;
    },
  });
}

/** The titles and rows of another sheet or row of titles; `null` until the person moves
 * away from what the version proposed. */
export function useColumnsPreview(id: string, choice: { sheet: string; headerRow: number } | null) {
  return useQuery({
    queryKey: tariffKeys.columns(id, choice?.sheet ?? '', choice?.headerRow ?? -1),
    queryFn: () => tariffsApi.columns(id, choice?.sheet ?? '', choice?.headerRow ?? 0),
    enabled: choice !== null,
    placeholderData: keepPreviousData,
  });
}

export function useVersionItems(id: string | undefined, offset: number, enabled = true) {
  return useQuery({
    queryKey: tariffKeys.items(id ?? '', offset),
    queryFn: () => tariffsApi.items(id ?? '', offset),
    enabled: Boolean(id) && enabled,
    placeholderData: keepPreviousData,
  });
}

export function useHistory(priceListId: string, enabled: boolean) {
  return useQuery({
    queryKey: tariffKeys.history(priceListId),
    queryFn: () => tariffsApi.history(priceListId),
    enabled,
  });
}

export function useTariffSearch(params: TariffSearchParams, enabled: boolean) {
  return useQuery({
    queryKey: tariffKeys.search(params),
    queryFn: () => tariffsApi.search(params),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useFamilies(supplierId?: string) {
  return useQuery({
    queryKey: tariffKeys.families(supplierId),
    queryFn: () => tariffsApi.families(supplierId),
    staleTime: 60_000,
  });
}

/**
 * After any write, what other screens show of it: the suppliers (the tariff in force, the
 * one on its way), the history, the search and its families. The version itself goes
 * straight into the cache from the answer.
 */
function refreshAround(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: tariffKeys.suppliers });
  void client.invalidateQueries({ queryKey: ['tariffs', 'history'] });
  void client.invalidateQueries({ queryKey: ['tariffs', 'search'] });
  void client.invalidateQueries({ queryKey: ['tariffs', 'families'] });
}

function useVersionMutation<TVariables>(mutationFn: (variables: TVariables) => Promise<Version>) {
  const client = useQueryClient();
  return useMutation<Version, ApiError, TVariables>({
    mutationFn,
    onSuccess: (version) => {
      client.setQueryData(tariffKeys.version(version.id), version);
      // The items of a version that was read again are not the ones in the cache.
      void client.removeQueries({ queryKey: [...tariffKeys.version(version.id), 'items'] });
      refreshAround(client);
    },
    // A refusal says something changed under the screen (somebody else published or
    // discarded it, the reading could not be asked for and the version failed): what is
    // shown is asked again, so the next click is on what is there now.
    onError: () => {
      void client.invalidateQueries({ queryKey: ['tariffs', 'version'] });
      refreshAround(client);
    },
  });
}

export function useCreatePriceList(supplierId: string) {
  return useVersionMutation<{ name: string; file: File }>(({ name, file }) =>
    tariffsApi.createPriceList(supplierId, name, file),
  );
}

export function useUploadVersion() {
  return useVersionMutation<{ priceListId: string; file: File }>(({ priceListId, file }) =>
    tariffsApi.uploadVersion(priceListId, file),
  );
}

export function useFromAttachment() {
  return useVersionMutation<FromAttachment>((payload) => tariffsApi.fromAttachment(payload));
}

export function useConfirmColumns(id: string) {
  return useVersionMutation<ColumnsWrite>((payload) => tariffsApi.confirmColumns(id, payload));
}

export function usePublish(id: string) {
  const client = useQueryClient();
  return useVersionMutation<PublishRequest>(async (payload) => {
    try {
      const version = await tariffsApi.publish(id, payload);
      // The notice it wrote (contract v10.50): already seen by whoever publishes, but in the
      // bell, with how the email to «Comerciales» went.
      void client.invalidateQueries({ queryKey: noticeKeys.all });
      return version;
    } catch (error) {
      // Refused because the email cannot go: the box asks again, and says why now.
      if (error instanceof ApiError && error.reason === 'salespeople_email') {
        void client.invalidateQueries({ queryKey: noticeKeys.salespeople });
      }
      throw error;
    }
  });
}

export function useDiscard(id: string) {
  return useVersionMutation<void>(() => tariffsApi.discard(id));
}

export function useRetry(id: string) {
  return useVersionMutation<void>(() => tariffsApi.retry(id));
}

function useSupplierMutation<TVariables>(mutationFn: (variables: TVariables) => Promise<Supplier>) {
  const client = useQueryClient();
  return useMutation<Supplier, ApiError, TVariables>({
    mutationFn,
    onSuccess: (supplier) => {
      client.setQueryData(tariffKeys.supplier(supplier.id), supplier);
      void client.invalidateQueries({
        queryKey: [...tariffKeys.suppliers, 'list'],
      });
      // The conditions are in every article, of the search and of each version (the usual
      // discount, the price with it, the maximum, the cost, the free shipping).
      void client.invalidateQueries({ queryKey: ['tariffs', 'search'] });
      void client.invalidateQueries({ queryKey: ['tariffs', 'version'] });
    },
  });
}

export function useCreateSupplier() {
  return useSupplierMutation<SupplierWrite>((payload) => tariffsApi.createSupplier(payload));
}

export function useUpdateSupplier(id: string) {
  return useSupplierMutation<SupplierUpdate>((payload) => tariffsApi.updateSupplier(id, payload));
}

export function usePutConditions(id: string) {
  return useSupplierMutation<SupplierConditionWrite[]>((conditions) =>
    tariffsApi.putConditions(id, conditions),
  );
}

/** A signed link to the file as it came, opened in a new tab. */
export function useOriginal() {
  return useMutation<string, ApiError, string>({
    mutationFn: async (versionId) => (await tariffsApi.original(versionId)).url,
  });
}
